import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Check, ClipboardList, IndianRupee, MessageSquare, Phone, RotateCcw, User, UserRoundCheck, X } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMerchantByQr } from '../actions/merchantActions';
import { createEnquiry } from '../actions/enquiryActions';

const API = process.env.REACT_APP_API_URL;

const inputBase = 'w-full border rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const inputCls = 'w-full border border-gray-300 rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const inputErr = `${inputBase} border-red-400`;
const maxDocumentFileSize = 5 * 1024 * 1024;

const emptyForm = {
  caseName: '',
  mobileNumber: '',
  referredBy: '',
  loanType: '',
  amountRequired: '',
  remarks: '',
  additionalDocumentType: '',
};

const additionalDocumentOptions = [
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'bill', label: 'Bill (Water/Electricity)' },
  { value: 'driving_licence', label: 'Driving Licence' },
];

const formDraftKey = qrToken => `loan-enquiry-draft:${qrToken || 'unknown'}`;

const loadFormDraft = qrToken => {
  try {
    const saved = sessionStorage.getItem(formDraftKey(qrToken));
    return saved ? { ...emptyForm, ...JSON.parse(saved) } : emptyForm;
  } catch {
    return emptyForm;
  }
};

const amountOk = v => v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0);
const phoneOk = v => /^\d{10}(?:\/\d{10})?$/.test(String(v || '').trim());
const sanitizePhoneNumbers = value => {
  const cleaned = String(value || '').replace(/[^\d/]/g, '');
  const slashIndex = cleaned.indexOf('/');
  const first = (slashIndex === -1 ? cleaned : cleaned.slice(0, slashIndex)).slice(0, 10);
  if (slashIndex === -1 || first.length < 10) return first;
  return `${first}/${cleaned.slice(slashIndex + 1).replace(/\//g, '').slice(0, 10)}`;
};

const rules = {
  caseName: v => v.trim() ? '' : 'Case name is required.',
  mobileNumber: v => phoneOk(v) ? '' : 'Enter 10 digits, or two 10-digit numbers separated by /.',
  amountRequired: v => amountOk(v) ? '' : 'Amount required must be >= 0.',
};

const duplicateEnquiryMessage = 'An enquiry with this mobile number already exists for this merchant.';

const fieldIcons = {
  caseName: User,
  mobileNumber: Phone,
  referredBy: UserRoundCheck,
  loanType: ClipboardList,
  amountRequired: IndianRupee,
  remarks: MessageSquare,
};

const FieldIcon = ({ field, multiline = false }) => {
  const Icon = fieldIcons[field];
  if (!Icon) return null;

  return (
    <Icon
      size={18}
      strokeWidth={2}
      className={`pointer-events-none absolute right-3 ${multiline ? 'top-3' : 'top-1/2 -translate-y-1/2'} text-gray-400`}
      aria-hidden="true"
    />
  );
};

const Field = ({ field, label, touched, errs, focusedField, children }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    {children}
    {touched[field] && focusedField !== field && errs[field] && <p className="text-xs text-red-600 mt-1">{errs[field]}</p>}
  </div>
);

const TextInput = ({ field, label, type = 'text', placeholder, form, touched, errs, focusedField, onChange, onBlur, onFocus }) => (
  <Field field={field} label={label} touched={touched} errs={errs} focusedField={focusedField}>
    <div className="relative">
      <input
        type={type}
        className={touched[field] && focusedField !== field && errs[field] ? inputErr : inputCls}
        placeholder={placeholder || label}
        value={form[field]}
        onChange={e => onChange(field, e.target.value)}
        onFocus={() => onFocus(field)}
        onBlur={e => onBlur(field, e.target.value)}
        inputMode={field === 'mobileNumber' ? 'tel' : undefined}
        maxLength={field === 'mobileNumber' ? 21 : undefined}
      />
      <FieldIcon field={field} />
    </div>
  </Field>
);

const AmountInput = props => (
  <TextInput {...props} type="number" placeholder="0" />
);

const documentLabels = {
  aadhaarImage: 'Aadhaar Front',
  aadhaarBackImage: 'Aadhaar Back',
  panCardImage: 'PAN Card',
  bankStatementImage: 'Bank Statement',
  additionalDocumentImage: 'Selected Document',
};

const CameraModal = ({ field, label, onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState('');
  const [capturedFile, setCapturedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    let stream;

    const startCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) {
        setError(err?.message || 'Camera is not available on this device.');
      }
    };

    startCamera();

    return () => {
      stream?.getTracks().forEach(track => track.stop());
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], `${field}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setCapturedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }, 'image/jpeg', 0.9);
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setCapturedFile(null);
    setPreviewUrl('');
  };

  const usePhoto = () => {
    if (!capturedFile) return;
    onCapture(field, capturedFile);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">Capture {label || documentLabels[field]}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl leading-none">&times;</button>
        </div>
        <div className="bg-black">
          {error ? (
            <div className="p-6 text-sm text-white text-center">{error}</div>
          ) : previewUrl ? (
            <img src={previewUrl} alt={`${label || documentLabels[field]} preview`} className="w-full aspect-[3/4] object-cover" />
          ) : (
            <video ref={videoRef} autoPlay playsInline muted className="w-full aspect-[3/4] object-cover" />
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>
        <div className="grid grid-cols-2 gap-2 p-4 sm:flex sm:justify-end">
          <button type="button" onClick={previewUrl ? retake : onClose} aria-label={previewUrl ? 'Retake photo' : 'Cancel'} title={previewUrl ? 'Retake' : 'Cancel'} className="inline-flex items-center justify-center border border-gray-300 hover:bg-gray-50 text-gray-700 w-11 h-11 rounded-lg transition-colors">
            {previewUrl ? <RotateCcw size={16} aria-hidden="true" /> : <X size={16} aria-hidden="true" />}
          </button>
          {!previewUrl && (
            <button type="button" onClick={capture} disabled={!!error} aria-label="Click photo" title="Click Photo" className="inline-flex items-center justify-center border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:border-gray-300 disabled:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed w-11 h-11 rounded-lg transition-colors">
              <Camera size={16} aria-hidden="true" />
            </button>
          )}
          {previewUrl && (
            <button type="button" onClick={onClose} aria-label="Cancel" title="Cancel" className="inline-flex items-center justify-center border border-gray-300 hover:bg-gray-50 text-gray-700 w-11 h-11 rounded-lg transition-colors">
              <X size={16} aria-hidden="true" />
            </button>
          )}
          {previewUrl && (
            <button type="button" onClick={usePhoto} aria-label="Use photo" title="Use Photo" className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white w-11 h-11 rounded-lg transition-colors">
              <Check size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function LoanForm({ qrTokenOverride = '', embedded = false, onSuccess }) {
  const { qrToken: routeQrToken } = useParams();
  const qrToken = qrTokenOverride || routeQrToken;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { merchant, loading: loadingMerchant, error: merchantError } = useSelector(s => s.merchantByQr || {});

  const [form, setForm] = useState(() => loadFormDraft(qrToken));
  const [formKey, setFormKey] = useState(0);
  const [errs, setErrs] = useState({});
  const [touched, setTouched] = useState({});
  const [documents, setDocuments] = useState({
    aadhaarImage: null,
    aadhaarBackImage: null,
    panCardImage: null,
    bankStatementImage: null,
    additionalDocumentImage: null,
  });
  const [cameraField, setCameraField] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [focusedField, setFocusedField] = useState('');
  const [products, setProducts] = useState([]);

  useEffect(() => { if (qrToken) dispatch(getMerchantByQr(qrToken)); }, [dispatch, qrToken]);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/products/public`)
      .then(({ data }) => {
        if (active) setProducts(data?.items || []);
      })
      .catch(() => {
        if (active) setProducts([]);
      });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    setForm(loadFormDraft(qrToken));
  }, [qrToken]);
  useEffect(() => {
    try {
      sessionStorage.setItem(formDraftKey(qrToken), JSON.stringify(form));
    } catch {}
  }, [form, qrToken]);
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const headerTitle = useMemo(() => {
    if (loadingMerchant) return 'Loading...';
    if (merchantError) return 'Invalid QR';
    if (merchant?.name) return merchant.name;
    return 'Loan Enquiry';
  }, [loadingMerchant, merchantError, merchant]);

  const onChange = (field, value) => {
    const nextValue = field === 'mobileNumber'
      ? sanitizePhoneNumbers(value)
      : value;
    setForm(prev => ({ ...prev, [field]: nextValue }));
  };
  const selectedAdditionalDocument = additionalDocumentOptions.find(option => option.value === form.additionalDocumentType);
  const selectedAdditionalDocumentLabel = selectedAdditionalDocument?.label || 'Selected Document';
  const onDocumentChange = (field, file) => {
    if (file && file.size > maxDocumentFileSize) {
      const label = field === 'additionalDocumentImage' ? selectedAdditionalDocumentLabel : documentLabels[field];
      setToast({ type: 'error', msg: `${label || 'Document'} image must be 5 MB or less.` });
      return;
    }
    setDocuments(prev => ({ ...prev, [field]: file }));
  };
  const onBlur = (field, value = form[field]) => {
    setFocusedField('');
    const nextValue = field === 'mobileNumber'
      ? sanitizePhoneNumbers(value)
      : value;
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrs(prev => ({ ...prev, [field]: rules[field] ? rules[field](nextValue) : '' }));
  };

  const validateAll = () => {
    const next = {};
    Object.keys(rules).forEach(key => {
      const msg = rules[key](form[key]);
      if (msg) next[key] = msg;
    });
    if (!qrToken) next.qrToken = 'Invalid QR link.';
    return next;
  };

  const submit = async e => {
    e.preventDefault();
    const nextErrs = validateAll();
    setErrs(nextErrs);
    setTouched(Object.fromEntries(Object.keys(rules).map(k => [k, true])));
    if (Object.keys(nextErrs).length) return;

    const hasDocuments = Object.values(documents).some(Boolean);
    let payload = { qrToken, ...form };

    if (hasDocuments) {
      const body = new FormData();
      body.append('qrToken', qrToken);
      Object.entries(form).forEach(([key, value]) => body.append(key, value));
      Object.entries(documents).forEach(([key, file]) => {
        if (file) body.append(key, file);
      });
      payload = body;
    }

    setSubmitting(true);
    try {
      const createdEnquiry = await dispatch(createEnquiry(payload));
      setToast({ type: 'success', msg: 'Thank you! Your enquiry was received.' });
      try {
        sessionStorage.removeItem(formDraftKey(qrToken));
      } catch {}
      setForm(emptyForm);
      setDocuments({ aadhaarImage: null, aadhaarBackImage: null, panCardImage: null, bankStatementImage: null, additionalDocumentImage: null });
      setTouched({});
      setErrs({});
      setFormKey(k => k + 1);
      if (embedded) {
        onSuccess?.(createdEnquiry);
      } else {
        setTimeout(() => {
          navigate(`/merchant-dashboard/${merchant._id}`);
        }, 1500);
      }
    } catch (err) {
      const message = err?.message || 'Failed to submit. Please try again.';
      if (message === duplicateEnquiryMessage) {
        setTouched(prev => ({ ...prev, mobileNumber: true }));
        setErrs(prev => ({ ...prev, mobileNumber: 'This mobile number has already submitted an enquiry for this merchant.' }));
      }
      setToast({ type: 'error', msg: message });
    } finally {
      setSubmitting(false);
    }
  };

  const inputProps = { form, touched, errs, focusedField, onChange, onBlur, onFocus: setFocusedField };

  return (
    <div className={embedded ? 'bg-gray-50 px-2 py-3 sm:p-5' : 'min-h-screen bg-gray-50 px-2 py-3 sm:p-6'}>
      {cameraField && (
        <CameraModal
          field={cameraField}
          label={cameraField === 'additionalDocumentImage' ? selectedAdditionalDocumentLabel : documentLabels[cameraField]}
          onCapture={onDocumentChange}
          onClose={() => setCameraField('')}
        />
      )}

      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-600'}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 break-words">{headerTitle}</h1>
        {loadingMerchant && <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />}
        {merchant?.status && (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 capitalize">{merchant.status}</span>
        )}
      </div>

      {merchantError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-6">
          This QR is invalid or inactive.
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-6 max-w-5xl">
        <form key={formKey} onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextInput field="caseName" label="Case Name *" {...inputProps} />
            <TextInput field="mobileNumber" label="Mobile Number *" placeholder="10 digits or 10 digits/10 digits" {...inputProps} />
            <TextInput field="referredBy" label="Ref.by" {...inputProps} />
            <Field field="loanType" label="Loan Type" touched={touched} errs={errs} focusedField={focusedField}>
              <div className="relative">
                <select
                  value={form.loanType}
                  onChange={event => onChange('loanType', event.target.value)}
                  onFocus={() => setFocusedField('loanType')}
                  onBlur={event => onBlur('loanType', event.target.value)}
                  className={`${inputCls} appearance-none`}
                >
                  <option value="">Select Loan Type</option>
                  {form.loanType && !products.some(product => product.name === form.loanType) && (
                    <option value={form.loanType}>{form.loanType}</option>
                  )}
                  {products.map(product => (
                    <option key={product._id} value={product.name}>{product.name}</option>
                  ))}
                </select>
                <FieldIcon field="loanType" />
              </div>
            </Field>
            <AmountInput field="amountRequired" label="Amount Required" {...inputProps} />
          </div>

          <Field field="remarks" label="Remarks" touched={touched} errs={errs} focusedField={focusedField}>
            <div className="relative">
              <textarea
                rows={3}
                className={inputCls}
                placeholder="Remarks"
                value={form.remarks}
                onChange={e => onChange('remarks', e.target.value)}
                onFocus={() => setFocusedField('remarks')}
                onBlur={e => onBlur('remarks', e.target.value)}
              />
              <FieldIcon field="remarks" multiline />
            </div>
          </Field>

          <button
            type="submit"
            disabled={submitting || loadingMerchant || !!merchantError}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-6 py-2.5 rounded-lg transition-colors"
          >
            {submitting ? 'Submitting...' : 'Submit Enquiry'}
          </button>
        </form>
      </div>
    </div>
  );
}
